import React from 'react';
import Cookies from 'js-cookie';
import { apiClient } from '../api';

/**
 * Base API URL - sourced from the shared axios client configuration
 */
const getBaseApiUrl = () => (apiClient.defaults.baseURL || '').replace(/\/$/, '');

/**
 * Creates a full image URL for patient pictures with auth token
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path (e.g., "tmp/patient_pics/7a12ba10-0297-4c01-afbf-7b4ff882868e_profile_pic.png")
 * @returns {string} - Complete image URL with auth token
 */
export const createPatientImageUrl = (patientId, imagePath) => {
  if (!patientId || !imagePath) {
    console.warn('createPatientImageUrl: Missing required parameters', { patientId, imagePath });
    return null;
  }

  // URL encode the image path to handle special characters and spaces
  const encodedImagePath = encodeURIComponent(imagePath);
  const baseApiUrl = getBaseApiUrl();
  
  // Get auth token
  const token = Cookies.get('access_token')
  // Construct the full URL with auth token
  const imageUrl = `${baseApiUrl}/api/v2/patient/${patientId}/pic?pic_path=${encodedImagePath}&token=${token}`;
  
  return imageUrl;
};

/**
 * Fetches an authenticated image and returns a blob URL
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path
 * @returns {Promise<string>} - Promise that resolves to blob URL
 */
export const fetchAuthenticatedImage = async (patientId, imagePath) => {
  if (!patientId) {
    throw new Error('Missing required parameters');
  }

  try {
    const token = Cookies.get('access_token')
    if (!token) {
      throw new Error('No authentication token available');
    }

    const encodedImagePath = encodeURIComponent(imagePath);
    const baseApiUrl = getBaseApiUrl();
    let imageUrl;
    if (imagePath) {
      imageUrl = `${baseApiUrl}/api/v2/patient/${patientId}/pic?pic_path=${encodedImagePath}`;
    } else {
      imageUrl = `${baseApiUrl}/api/v2/patient/${patientId}/pic`;
    }

    const response = await fetch(imageUrl, {
      method: 'GET',
      headers: {
        'Authorization': token,
        'Accept': 'image/*',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    return blobUrl;
  } catch (error) {
    console.error('Error fetching authenticated image:', error);
    throw error;
  }
};

/**
 * Creates a profile picture URL specifically
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path
 * @returns {Promise<string>} - Promise that resolves to blob URL
 */
export const createPatientProfilePicUrl = async (patientId, imagePath) => {
  return fetchAuthenticatedImage(patientId, imagePath);
};

/**
 * Creates a reference picture URL specifically
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path
 * @returns {Promise<string>} - Promise that resolves to blob URL
 */
export const createPatientReferencePicUrl = async (patientId, imagePath) => {
  return fetchAuthenticatedImage(patientId, imagePath);
};

/**
 * Hook for managing authenticated images with caching
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path
 * @returns {object} - Object containing imageUrl, loading, and error states
 */
export const useAuthenticatedImage = (patientId, imagePath) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!patientId || !imagePath) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const blobUrl = await fetchAuthenticatedImage(patientId, imagePath);
        
        if (isMounted) {
          setImageUrl(blobUrl);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
          console.error('Error loading authenticated image:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup function to revoke blob URL
    return () => {
      isMounted = false;
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [patientId, imagePath]);

  return { imageUrl, loading, error };
};

/**
 * Default placeholder image URL for patients
 */
export const DEFAULT_PATIENT_PLACEHOLDER = '/assets/default-patient-avatar.png';

/**
 * Cache for authenticated images to avoid repeated requests
 */
const imageCache = new Map();

/**
 * Fetches authenticated image with caching
 * @param {string} patientId - The patient ID
 * @param {string} imagePath - The image path
 * @returns {Promise<string>} - Promise that resolves to cached blob URL
 */
export const fetchAuthenticatedImageWithCache = async (patientId, imagePath) => {
  const cacheKey = `${patientId}-${imagePath}`;
  
  // Check if image is already cached
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey);
  }

  try {
    const blobUrl = await fetchAuthenticatedImage(patientId, imagePath);
    
    // Cache the blob URL
    imageCache.set(cacheKey, blobUrl);
    
    // Set up cleanup after 5 minutes to prevent memory leaks
    setTimeout(() => {
      if (imageCache.has(cacheKey)) {
        const cachedUrl = imageCache.get(cacheKey);
        URL.revokeObjectURL(cachedUrl);
        imageCache.delete(cacheKey);
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return blobUrl;
  } catch (error) {
    console.error('Error fetching cached authenticated image:', error);
    throw error;
  }
};

/**
 * Clears the image cache
 */
export const clearImageCache = () => {
  imageCache.forEach(url => URL.revokeObjectURL(url));
  imageCache.clear();
};